import { Component, Inject, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';

import { SupportTicket, TicketStatus, TICKET_STATUSES, TICKET_PRIORITIES, TicketPriority, ticketPriority } from '@shared/models/entities/supportTicket.model';

/** Usuario asignable (los de Epione que resuelven los tickets). */
export interface TicketAssignee {
  id:   string;
  name: string;
}

export interface TicketActionsDialogData {
  ticket:    SupportTicket;
  assignees: TicketAssignee[];
}

export interface TicketActionsResult {
  status:         TicketStatus;
  priority:       TicketPriority | null;
  estimatedHours: number | null;
  assignedTo:     string | null;
  assignedToName: string | null;
  resolution:     string | null;
}

/**
 * Acciones del Administrador Epione sobre un ticket: estimado de horas,
 * responsable y estado (Pendiente → En proceso → Solucionado).
 *
 * Solo captura y devuelve; el guardado lo hace la pantalla para poder
 * refrescar la tabla y mostrar el mensaje una sola vez.
 */
@Component({
  selector: 'app-ticket-actions-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MaterialModule],
  template: `
    <h2 mat-dialog-title>
      <mat-icon>build</mat-icon>
      Acciones del ticket {{ data.ticket.folio }}
    </h2>

    <mat-dialog-content class="mat-typography">

      <div class="ticket-summary">
        <p class="summary-subject">{{ data.ticket.subject }}</p>
        <p class="summary-meta">
          <span><b>Módulo:</b> {{ data.ticket.module }}</span>
          <span><b>Parte:</b> {{ data.ticket.location || 'No especificado' }}</span>
          <span><b>Creado:</b> {{ data.ticket.createdOn }}</span>
          <span><b>Por:</b> {{ data.ticket.requestedByName || 'Sin nombre' }}</span>
        </p>
        <p class="summary-description">{{ data.ticket.description }}</p>
      </div>

      <form [formGroup]="form" class="actions-form">

        <mat-form-field appearance="outline">
          <mat-label>Prioridad</mat-label>
          <mat-select formControlName="priority">
            @for (p of priorities; track p.id) {
              <mat-option [value]="p.id">{{ p.name }}</mat-option>
            }
          </mat-select>
          @if (selectedPriority; as sp) {<mat-hint>Respuesta: {{ sp.response }}</mat-hint>}
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Tiempo estimado (resolución)</mat-label>
          <input matInput [value]="selectedPriority?.resolution ?? 'Elige la prioridad'" disabled>
          <mat-icon matSuffix>schedule</mat-icon>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Estado</mat-label>
          <mat-select formControlName="status">
            @for (status of statuses; track status.id) {
              <mat-option [value]="status.id">{{ status.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-col">
          <mat-label>Asignado a (Administrador Epione)</mat-label>
          <mat-select formControlName="assignedTo">
            <mat-option [value]="null">Sin asignar</mat-option>
            @for (person of data.assignees; track person.id) {
              <mat-option [value]="person.id">{{ person.name }}</mat-option>
            }
          </mat-select>
          <mat-hint>Al asignar responsable el estado pasa solo a "En proceso" y le llega la notificación.</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-col">
          <mat-label>Notas de resolución</mat-label>
          <textarea matInput rows="4" formControlName="resolution"
                    placeholder="Qué se hizo para resolverlo"></textarea>
        </mat-form-field>

      </form>

    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-raised-button mat-dialog-close>Cancelar</button>
      <button mat-raised-button color="accent" (click)="save()">Guardar</button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2 { display: flex; align-items: center; gap: 8px; color: #170f5f; font-weight: bold; }
    .ticket-summary {
      background: #fafafa; border: 1px solid #e0e0e0; border-radius: 8px;
      padding: 14px 16px; margin-bottom: 18px;
    }
    .summary-subject { margin: 0 0 6px; font-weight: 700; color: #170f5f; font-size: 15px; }
    .summary-meta {
      display: flex; flex-wrap: wrap; gap: 4px 18px;
      margin: 0 0 10px; font-size: 12px; color: #555555;
    }
    .summary-description { margin: 0; font-size: 13px; color: #170f5f; white-space: pre-line; }
    .actions-form { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 16px; }
    .actions-form mat-form-field { width: 100%; }
    .full-col { grid-column: 1 / -1; }
    @media (max-width: 600px) { .actions-form { grid-template-columns: 1fr; } }
  `],
})
export class TicketActionsDialogComponent {

  private formBuilder = inject(FormBuilder);

  readonly statuses = TICKET_STATUSES;
  readonly priorities = TICKET_PRIORITIES;
  get selectedPriority() { return ticketPriority(this.form.get('priority')?.value); }

  form: FormGroup;

  constructor(
    public dialogRef: MatDialogRef<TicketActionsDialogComponent, TicketActionsResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: TicketActionsDialogData,
  ) {
    this.form = this.formBuilder.group({
      priority:       [data.ticket.priority ?? null],
      status:         [data.ticket.status ?? 'PENDIENTE', Validators.required],
      assignedTo:     [data.ticket.assignedTo ?? null],
      resolution:     [data.ticket.resolution ?? null],
    });
    // Al asignar responsable el ticket pasa a ASIGNADO; si se quita, regresa a PENDIENTE.
    this.form.get('assignedTo')?.valueChanges.subscribe(v => {
      const status = this.form.get('status')?.value;
      if (v && status === 'PENDIENTE') this.form.get('status')?.setValue('ASIGNADO');
      if (!v && status === 'ASIGNADO') this.form.get('status')?.setValue('PENDIENTE');
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value;
    const assignee = this.data.assignees.find(a => a.id === value.assignedTo) ?? null;
    // El tiempo estimado es el de resolución de la prioridad.
    const hours = ticketPriority(value.priority)?.hours ?? this.data.ticket.estimatedHours ?? null;

    // Regla de estado: con responsable y aún PENDIENTE → ASIGNADO.
    let status = value.status as TicketStatus;
    if (assignee && status === 'PENDIENTE') status = 'ASIGNADO';
    if (!assignee && status === 'ASIGNADO') status = 'PENDIENTE';

    this.dialogRef.close({
      status,
      priority:       (value.priority ?? null) as TicketPriority | null,
      estimatedHours: hours,
      assignedTo:     assignee?.id ?? null,
      assignedToName: assignee?.name ?? null,
      resolution:     value.resolution ? String(value.resolution).trim() : null,
    });
  }
}
